import React, { useEffect, useRef, useState } from "react";
import {
  FiSearch,
  FiBarChart2,
  FiLink2,
  FiZap,
  FiInfo,
  FiLock,
} from "react-icons/fi";

/**
 * GroundingToggle - Click-to-expand benefit panel cho toggle "Chế độ chuyên sâu"
 *
 * Props:
 *   - useGoogleSearch: bool (controlled)
 *   - onChange: fn(newValue) (required)
 *   - id: string (optional, default "useGoogleSearch") - DOM id cho checkbox
 *   - contextLabel: string (optional) - context hiển thị trong mô tả phụ, vd "bài viết WordPress"
 *   - isPlanEligible: bool (optional, default true) - false sẽ hiển thị gate UI
 *   - onUpgradeClick: fn (optional) - click handler cho button "Nâng cấp" trong gate UI
 *
 * State nội bộ:
 *   - isExpanded: bool, persist trong localStorage key "autowork.grounding.expanded.v1"
 *   - First-visit auto-expand: nếu "autowork.grounding.seen.v1" chưa tồn tại
 *
 * Behavior:
 *   - Lần đầu user gặp component → panel auto-expand + mark "seen"
 *   - Click toàn dòng (text label, icon, badge, mô tả) → toggle expand/collapse
 *   - Click info button ⓘ → toggle expand/collapse
 *   - Click toggle (vòng tròn xanh hoặc checkbox) → chỉ on/off, KHÔNG expand
 *   - ESC đóng expand khi panel focus
 *   - Nếu isPlanEligible=false → render gate UI (không toggle được)
 *
 * Honest behavior: AI Gemini 3.1 Flash Lite TỰ QUYẾT ĐỊNH khi nào search.
 * Docs verified 2026-05-18: https://ai.google.dev/gemini-api/docs/google-search
 */

const STORAGE_KEY = "autowork.grounding.expanded.v1";
const STORAGE_SEEN_KEY = "autowork.grounding.seen.v1";

const BENEFITS = [
  { Icon: FiBarChart2, label: "Số liệu thật" },
  { Icon: FiLink2, label: "Có nguồn" },
  { Icon: FiZap, label: "Tin mới nhất" },
];

// Ví dụ đa ngành phục vụ user non-tech (công nghệ, tài chính, đời sống)
const EXAMPLES = [
  {
    domain: "Công nghệ",
    bad: '"iPhone 16 ra mắt năm 2023"',
    badNote: "(AI đoán — sai)",
    good: '"iPhone 16 ra mắt năm 2024"',
    goodNote: "(Google search — đúng)",
  },
  {
    domain: "Tài chính",
    bad: '"Tỷ giá USD khoảng 24.000đ"',
    badNote: "(AI đoán — cũ)",
    good: '"Tỷ giá USD hôm nay theo Vietcombank"',
    goodNote: "(Google search — mới nhất)",
  },
  {
    domain: "Đời sống",
    bad: '"Giá xăng RON95 khoảng 22.000đ"',
    badNote: "(AI đoán — cũ)",
    good: '"Giá xăng RON95 hôm nay theo Petrolimex"',
    goodNote: "(Google search — cập nhật)",
  },
];

const COST_NOTE = "Tốn thêm 10 điểm mỗi lần dùng";
const WARNING_TEXT =
  "AI tìm kiếm và phân tích chuyên sâu — không phải lúc nào cũng dùng tool. Bài về tin tức, số liệu, sự kiện gần đây sẽ hưởng lợi nhiều nhất.";
const WHEN_TO_DISABLE_TEXT =
  "Bài cảm xúc, truyện cá nhân, hoặc content evergreen không cần số liệu mới. Tắt đi để tiết kiệm điểm.";
const SOCIAL_PROOF = "Được 12.000+ user AutoWork tin dùng";

function readExpandedFromStorage() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch (_e) {
    return false;
  }
}

function writeExpandedToStorage(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
  } catch (_e) {
    // private mode / quota exceeded — ignore
  }
}

const GroundingToggle = ({
  useGoogleSearch,
  onChange,
  id = "useGoogleSearch",
  contextLabel = "bài viết",
  isPlanEligible = true,
  onUpgradeClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(readExpandedFromStorage);
  const panelRef = useRef(null);
  const toggleWrapperRef = useRef(null);

  // Mark "seen" 1 lần khi component mount
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_SEEN_KEY, "1");
    } catch (_e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    writeExpandedToStorage(isExpanded);
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setIsExpanded(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isExpanded]);

  const handleRowClick = (e) => {
    // Skip if click is inside the toggle wrapper (checkbox or its visual label)
    if (
      toggleWrapperRef.current &&
      toggleWrapperRef.current.contains(e.target)
    ) {
      return;
    }
    setIsExpanded((v) => !v);
  };

  const handleRowKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      // Skip if focus is on the toggle checkbox
      if (
        toggleWrapperRef.current &&
        toggleWrapperRef.current.contains(document.activeElement)
      ) {
        return;
      }
      e.preventDefault();
      setIsExpanded((v) => !v);
    }
  };

  const handleToggleChange = (e) => {
    e.stopPropagation();
    const checked = e.target.checked;
    onChange(checked);
    if (!checked) {
      setIsExpanded(false);
    }
  };

  const handleUpgradeClick = (e) => {
    e.stopPropagation();
    if (typeof onUpgradeClick === "function") {
      onUpgradeClick();
    }
  };

  // Plan-gate UI: khi user chưa đủ gói Cao Cấp / Doanh Nghiệp
  if (!isPlanEligible) {
    return (
      <div
        data-testid="grounding-toggle-wrapper"
        data-testid-locked="true"
        className="rounded-md p-3 border border-slate-200 bg-slate-50 shadow-sm"
      >
        <div
          data-testid="grounding-row-locked"
          className="flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2 flex-1">
            <FiLock
              className="w-4 h-4 text-slate-500 shrink-0"
              aria-hidden="true"
            />
            <span className="text-gray-700 font-medium">
              Chế độ chuyên sâu
            </span>
            <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full">
              🔒 Cần gói Cao Cấp
            </span>
          </div>
          <button
            type="button"
            onClick={handleUpgradeClick}
            data-testid="grounding-upgrade-button"
            aria-label="Nâng cấp gói để dùng chế độ chuyên sâu"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-2 py-1"
          >
            Nâng cấp →
          </button>
        </div>
        <p
          id={`${id}-locked-helper`}
          className="mt-2 text-xs text-slate-600"
        >
          Chế độ chuyên sâu có trong gói Cao Cấp trở lên. AI sẽ tự quyết khi nào dùng
          thông tin mới nhất để bài viết chính xác hơn.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="grounding-toggle-wrapper"
      className="rounded-md p-3 border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm"
    >
      <div
        data-testid="grounding-row"
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`${id}-panel`}
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
        className="flex items-center justify-between gap-3 cursor-pointer select-none"
      >
        <div className="flex items-center gap-2 flex-1">
          <div className="text-gray-700 font-medium flex items-center gap-1">
            <FiSearch
              className="w-4 h-4 text-blue-600"
              aria-hidden="true"
            />
            <span>Chế độ chuyên sâu</span>
            <span
              className="text-sm font-semibold bg-amber-200 text-amber-900 px-2.5 py-1 rounded-full cursor-help"
              title="Tốn thêm 10 điểm cho mỗi lần tạo bài ở chế độ chuyên sâu"
            >
              +10 điểm
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((v) => !v);
            }}
            aria-label="Giải thích: Chế độ chuyên sâu"
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-400 outline-none transition-colors hover:text-blue-500 focus-visible:text-blue-500"
          >
            <FiInfo className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
        <div
          ref={toggleWrapperRef}
          className="relative inline-block align-middle select-none shrink-0"
          data-testid="grounding-toggle"
        >
          <input
            type="checkbox"
            name="useGoogleSearch"
            id={id}
            checked={useGoogleSearch}
            onChange={handleToggleChange}
            onClick={(e) => e.stopPropagation()}
            aria-label="Chế độ chuyên sâu"
            aria-describedby={`${id}-cost-note`}
            className="hidden"
          />
          <label
            htmlFor={id}
            onClick={(e) => e.stopPropagation()}
            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
            style={{
              backgroundColor: useGoogleSearch ? "#3b82f6" : "#e5e7eb",
            }}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                useGoogleSearch ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </label>
        </div>
      </div>

      <div
        id={`${id}-panel`}
        ref={panelRef}
        data-testid="grounding-panel"
        hidden={!isExpanded}
        className="mt-3 space-y-3 text-sm"
      >
        <div>
          <p className="font-medium text-gray-700 mb-2">Bạn sẽ được gì?</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {BENEFITS.map(({ Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-blue-100"
              >
                <Icon
                  className="w-4 h-4 text-blue-600 shrink-0"
                  aria-hidden="true"
                />
                <span className="text-gray-700">{label}</span>
              </div>
            ))}
          </div>
        </div>



        <div>
          <p className="font-medium text-gray-700 mb-2">Ví dụ theo ngành</p>
          <div className="space-y-3">
            {EXAMPLES.map((ex) => (
              <div
                key={ex.domain}
                className="rounded-md border border-blue-100 bg-white p-2.5"
              >
                <p className="text-xs font-semibold text-blue-700 mb-1">
                  {ex.domain}
                </p>
                <p className="text-gray-700">
                  <span className="text-red-700 line-through font-medium">
                    {ex.bad}
                  </span>{" "}
                  <span className="text-xs text-gray-500">{ex.badNote}</span>
                </p>
                <p className="text-gray-700">
                  <span className="text-green-700 font-medium">{ex.good}</span>{" "}
                  <span className="text-xs text-gray-500">{ex.goodNote}</span>
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
          <p className="font-medium text-slate-800 mb-1">
            💡 Khi nào nên TẮT?
          </p>
          <p className="text-slate-600 leading-relaxed">
            {WHEN_TO_DISABLE_TEXT}
          </p>
        </div>

        <div
          className="flex items-center gap-1.5 text-xs text-blue-700"
          data-testid="grounding-social-proof"
        >
          <span aria-hidden="true">⭐</span>
          <span>{SOCIAL_PROOF}</span>
        </div>

        <p
          id={`${id}-cost-note`}
          data-testid="grounding-cost-note"
          className="text-xs text-gray-600 pt-1 border-t border-blue-100"
        >
          💰 {COST_NOTE} cho {contextLabel}
        </p>
      </div>
    </div>
  );
};

export default GroundingToggle;
